import { useLocation } from "wouter";
import { VIEW_PATH, pathToViewMode } from "../../shared/types/view-routes";
import type { ViewMode } from "../../shared/types";
import {
  Sun,
  CalendarIcon,
  CheckSquare,
  Flame,
  ListIcon,
  BarChart3,
  ClockIcon,
  CalendarWeekIcon,
  LayoutGridIcon,
  SettingsIcon,
  SearchIcon,
  Logo,
  GridIcon,
  BookIcon,
  KanbanIcon,
  WrenchIcon,
  BugIcon,
} from "../icons";


interface DesktopBottomBarProps {
  readonly advancedMode: boolean;
  readonly hiddenSections: ReadonlySet<ViewMode>;
  readonly onSearch: () => void;
  readonly onSettings: () => void;
  readonly onBugReport: () => void;
  readonly onAdmin?: () => void;
  readonly onDev?: () => void;
}

interface IconProps {
  readonly className?: string;
}

interface NavItem {
  readonly mode: ViewMode;
  readonly label: string;
  readonly icon: (props: IconProps) => React.ReactNode;
}

const STANDARD_ITEMS: ReadonlyArray<NavItem> = [
  { mode: "today", label: "Aujourd'hui", icon: Sun },
  { mode: "tasks", label: "Tâches", icon: CheckSquare },
  { mode: "habits", label: "Habitudes", icon: Flame },
  { mode: "calendar", label: "Calendrier", icon: CalendarIcon },
  { mode: "agenda", label: "Agenda", icon: ListIcon },
  { mode: "weekly", label: "Semaine", icon: CalendarWeekIcon },
  { mode: "schedule", label: "Emploi du temps", icon: LayoutGridIcon },
  { mode: "stats", label: "Stats", icon: BarChart3 },
  { mode: "free-slots", label: "Créneaux libres", icon: ClockIcon },
];

const ADVANCED_ITEMS: ReadonlyArray<NavItem> = [
  { mode: "kanban", label: "Kanban", icon: KanbanIcon },
  { mode: "eisenhower", label: "Eisenhower", icon: GridIcon },
  { mode: "journal", label: "Journal", icon: BookIcon },
];

export function DesktopBottomBar({
  advancedMode,
  hiddenSections,
  onSearch,
  onSettings,
  onBugReport,
  onAdmin,
  onDev,
}: DesktopBottomBarProps) {
  const [location, navigate] = useLocation();
  const active = pathToViewMode(location);

  const navItems = (advancedMode
    ? [...STANDARD_ITEMS, ...ADVANCED_ITEMS]
    : STANDARD_ITEMS
  ).filter((item) => !hiddenSections.has(item.mode));

  return (
    <div className="hidden md:flex items-center h-12 bg-surface border-t border-border px-3 gap-1 shrink-0">
      {/* Logo */}
      <button
        onClick={() => navigate(VIEW_PATH.today)}
        className="shrink-0 flex items-center justify-center w-9 h-9 rounded-lg hover:bg-surface-alt transition-colors mr-2"
        title="Aujourd'hui"
      >
        <Logo />
      </button>

      <div className="h-6 w-px bg-border/50 shrink-0" />

      {/* Navigation */}
      <nav className="flex-1 flex items-center justify-center gap-0.5 overflow-x-auto scrollbar-hide">
        {navItems.map(({ mode, label, icon: Icon }) => (
          <button
            key={mode}
            onClick={() => navigate(VIEW_PATH[mode])}
            className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors shrink-0 ${
              active === mode
                ? "bg-accent/10 text-accent"
                : "text-ink-muted hover:text-ink hover:bg-surface-alt"
            }`}
            title={label}
          >
            <Icon className="w-4 h-4" />
          </button>
        ))}
      </nav>

      <div className="h-6 w-px bg-border/50 shrink-0" />

      {/* Utilities */}
      <div className="flex items-center gap-0.5 shrink-0 ml-2">
        <button
          onClick={onSearch}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-ink-muted hover:text-ink hover:bg-surface-alt transition-colors"
          title="Rechercher"
        >
          <SearchIcon className="w-4 h-4" />
        </button>

        {onAdmin && (
          <button
            onClick={onAdmin}
            className="flex items-center justify-center w-9 h-9 rounded-lg text-violet-500 hover:text-violet-400 hover:bg-violet-500/10 transition-colors"
            title="Administration"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/></svg>
          </button>
        )}

        <button
          onClick={onBugReport}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-ink-muted hover:text-ink hover:bg-surface-alt transition-colors"
          title="Signaler un bug"
        >
          <BugIcon className="w-4 h-4" />
        </button>

        <button
          onClick={onSettings}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-ink-muted hover:text-ink hover:bg-surface-alt transition-colors"
          title="Paramètres"
        >
          <SettingsIcon className="w-4 h-4" />
        </button>

        {onDev && (
          <button
            onClick={onDev}
            className="flex items-center justify-center w-9 h-9 rounded-lg text-amber-500/70 hover:text-amber-500 hover:bg-amber-500/10 transition-colors"
            title="Dev Tools"
          >
            <WrenchIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
